import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import {
  validateAppointmentBookingReceipt,
  type AppointmentBookingReceiptV2,
} from "@/app/booking/core/appointmentBookingReceipt";
import {
  createAppointmentBookingReceiptStrictPersisted,
  inspectAppointmentBookingReceiptPersisted,
  removeAppointmentBookingReceiptStrictPersisted,
  storeAppointmentBookingReceipt,
} from "@/app/booking/core/appointmentBookingReceiptStore";
import { createVersionedSessionDraftStore } from "@/app/booking/core/versionedSessionDraftStore";
import {
  validateAppointmentWaitlistReceipt,
  validateRetiredGroupAppointmentWaitlistReceiptForStorageMigration,
  type AppointmentWaitlistReceiptV3,
} from "@/app/booking/waitlist/appointmentWaitlistReceipt";
import {
  createAppointmentWaitlistReceiptStrictPersisted,
  inspectAppointmentWaitlistReceiptPersisted,
  removeAppointmentWaitlistReceiptStrictPersisted,
  storeAppointmentWaitlistReceipt,
} from "@/app/booking/waitlist/appointmentWaitlistReceiptStore";
import {
  validatePackagePurchaseReceipt,
  type PackagePurchaseReceipt,
} from "@/app/commerce/packages/packagePurchaseReceipt";
import {
  createPackagePurchaseReceiptStrictPersisted,
  inspectPackagePurchaseReceiptPersisted,
  removePackagePurchaseReceiptStrictPersisted,
  storePackagePurchaseReceipt,
} from "@/app/commerce/packages/packagePurchaseReceiptStore";

import {
  SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
  canActivatePublicSpecialistLocationGateway,
} from "./activation";
import {
  createSpecialistLocationGatewayOwnerKey,
} from "./canonicalKey";
import {
  validateSpecialistLocationGatewayNavigationAuthority,
  type SpecialistLocationGatewayCommittedNavigationLineageV1,
  type SpecialistLocationGatewayDestinationKind,
} from "./contract";
import {
  readSpecialistLocationGatewayAppointmentLineageByEvidence,
  readSpecialistLocationGatewayPackageDestinationPair,
} from "./destinationLineage";
import {
  createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint,
  createSpecialistLocationGatewayPackageDestinationFingerprint,
} from "./destinationTransactions";
import {
  beginSpecialistLocationGatewayTerminalWrite,
  completeSpecialistLocationGatewayTerminalWrite,
  specialistLocationGatewayTerminalWriteIsCurrent,
} from "./terminalWriteCoordinator";
import { migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage } from "@/app/booking/waitlist/retiredGroupAppointmentWaitlistStorageMigration";
import { SPECIALIST_LOCATION_GATEWAY_WAITLIST_TERMINAL_ORIGIN_STORAGE_MAX_SERIALIZED_CHARACTERS } from "@/app/booking/waitlist/appointmentWaitlistStorageLimits";

export const SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_SCHEMA_VERSION =
  1 as const;
export const SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_STORAGE_MAX_AGE_MS =
  24 * 60 * 60 * 1_000 + 10 * 60 * 1_000;
export const SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_CONFIRMATION_ORIGIN_STORAGE_KEY =
  "volera:specialist-location-gateway-terminal-origins:appointment-confirmation:v1";
export const SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_WAITLIST_ORIGIN_STORAGE_KEY =
  "volera:specialist-location-gateway-terminal-origins:appointment-waitlist:v1";
export const SPECIALIST_LOCATION_GATEWAY_PACKAGE_PURCHASE_ORIGIN_STORAGE_KEY =
  "volera:specialist-location-gateway-terminal-origins:package-purchase:v1";

export type SpecialistLocationGatewayTerminalKind =
  | "appointment-confirmation"
  | "appointment-waitlist"
  | "package-purchase";

type SupportedTerminalReceipt =
  | AppointmentBookingReceiptV2
  | AppointmentWaitlistReceiptV3
  | PackagePurchaseReceipt;

export interface SpecialistLocationGatewayTerminalOriginV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_SCHEMA_VERSION;
  kind: "specialist-location-gateway-terminal-origin";
  draftId: string;
  ownerKey: string;
  terminalKind: SpecialistLocationGatewayTerminalKind;
  activationRevision: typeof SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION;
  receiptFingerprint: string;
  customerAccountId: string;
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  destinationDraftFingerprint: string;
  sourceAppointmentDraftFingerprint: string | null;
  terminalAtEpochMs: number;
  /**
   * Authoritative immutable terminal evidence. The legacy receipt store is a
   * compatibility projection and may be repaired from this exact snapshot.
   */
  receipt: SupportedTerminalReceipt;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
}

type PersistedReceiptInspection<TReceipt> =
  | { status: "absent" }
  | { status: "present"; draft: TReceipt }
  | { status: "unreadable" };

type PersistedReceiptCreation<TReceipt> =
  | { status: "created"; draft: TReceipt }
  | { status: "conflict"; reason: "target-present" }
  | { status: "capacity" }
  | {
      status:
        | "invalid"
        | "unreadable"
        | "persistence-failed"
        | "readback-mismatch";
    };

type PersistedReceiptRemoval<TReceipt> =
  | { status: "removed"; draft: TReceipt }
  | { status: "absent" }
  | { status: "conflict"; reason: "target-mismatch" }
  | {
      status:
        | "invalid"
        | "unreadable"
        | "persistence-failed"
        | "readback-mismatch";
    };

export interface SpecialistLocationGatewayTerminalWriteOwner {
  readonly requiresGatewayOrigin: boolean;
  readonly isCurrentOwner: () => boolean;
}

const TERMINAL_RECEIPT_FINGERPRINT_PREFIX =
  "specialist-location-gateway-terminal-receipt-v1-";
const MAX_SERIALIZED_MARKERS =
  SPECIALIST_LOCATION_GATEWAY_WAITLIST_TERMINAL_ORIGIN_STORAGE_MAX_SERIALIZED_CHARACTERS;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]) {
  const actual = Object.keys(value).sort();
  const canonical = [...expected].sort();
  return (
    actual.length === canonical.length &&
    actual.every((key, index) => key === canonical[index])
  );
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

function isEpochMilliseconds(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 8_640_000_000_000_000
  );
}

function hashSerializableSnapshot(value: string, seed: number) {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16_777_619);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x846ca68b);
  hash ^= hash >>> 16;
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function createTerminalReceiptFingerprint(
  terminalKind: SpecialistLocationGatewayTerminalKind,
  receipt: SupportedTerminalReceipt,
) {
  const serialized = JSON.stringify([terminalKind, receipt]);
  const digest = [
    0x811c9dc5,
    0x9e3779b9,
    0x85ebca6b,
    0xc2b2ae35,
    0x27d4eb2f,
    0x165667b1,
    0xd3a2646c,
    0xfd7046c5,
  ]
    .map((seed) => hashSerializableSnapshot(serialized, seed))
    .join("");
  return `${TERMINAL_RECEIPT_FINGERPRINT_PREFIX}${digest}`;
}

function isTerminalReceiptFingerprint(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^specialist-location-gateway-terminal-receipt-v1-[a-f0-9]{64}$/.test(
      value,
    )
  );
}

function cloneCommittedLineage(value: unknown) {
  const validation = validateSpecialistLocationGatewayNavigationAuthority(value);
  return validation.valid &&
    validation.draft.kind ===
      "specialist-location-gateway-navigation-lineage"
    ? validation.draft
    : null;
}

function validateTerminalOriginForWaitlistMode(
  value: unknown,
  expectedWaitlistMode: "individual" | "group",
):
  | { valid: true; draft: SpecialistLocationGatewayTerminalOriginV1 }
  | { valid: false; reason: "invalid-specialist-location-gateway-terminal-origin" } {
  try {
    if (
      !isRecord(value) ||
      !hasExactKeys(value, [
        "schemaVersion",
        "kind",
        "draftId",
        "ownerKey",
        "terminalKind",
        "activationRevision",
        "receiptFingerprint",
        "customerAccountId",
        "destinationKind",
        "destinationDraftId",
        "destinationDraftFingerprint",
        "sourceAppointmentDraftFingerprint",
        "terminalAtEpochMs",
        "receipt",
        "lineage",
      ]) ||
      value.schemaVersion !==
        SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_SCHEMA_VERSION ||
      value.kind !== "specialist-location-gateway-terminal-origin" ||
      !isIdentifier(value.draftId) ||
      !isIdentifier(value.customerAccountId) ||
      (value.terminalKind !== "appointment-confirmation" &&
        value.terminalKind !== "appointment-waitlist" &&
        value.terminalKind !== "package-purchase") ||
      value.activationRevision !==
        SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION ||
      !isTerminalReceiptFingerprint(value.receiptFingerprint) ||
      (value.destinationKind !== "individual" &&
        value.destinationKind !== "package" &&
        value.destinationKind !== "group") ||
      !isIdentifier(value.destinationDraftId) ||
      typeof value.destinationDraftFingerprint !== "string" ||
      (value.sourceAppointmentDraftFingerprint !== null &&
        typeof value.sourceAppointmentDraftFingerprint !== "string") ||
      !isEpochMilliseconds(value.terminalAtEpochMs)
    ) {
      return {
        valid: false,
        reason: "invalid-specialist-location-gateway-terminal-origin",
      };
    }
    const lineage = cloneCommittedLineage(value.lineage);
    const receipt = cloneTerminalReceipt(
      value.terminalKind,
      value.receipt,
      expectedWaitlistMode,
    );
    const receiptBinding = receipt
      ? resolveTerminalReceiptBinding(value.terminalKind, receipt)
      : null;
    if (
      !lineage ||
      !receipt ||
      !receiptBinding ||
      receipt.draftId !== value.draftId ||
      createTerminalReceiptFingerprint(value.terminalKind, receipt) !==
        value.receiptFingerprint ||
      receiptBinding.customerAccountId !== value.customerAccountId ||
      receiptBinding.destinationKind !== value.destinationKind ||
      receiptBinding.destinationDraftId !== value.destinationDraftId ||
      receiptBinding.destinationDraftFingerprint !==
        value.destinationDraftFingerprint ||
      receiptBinding.sourceAppointmentDraftFingerprint !==
        value.sourceAppointmentDraftFingerprint ||
      receiptBinding.terminalAtEpochMs !== value.terminalAtEpochMs ||
      lineage.customerAccountId !== value.customerAccountId ||
      lineage.destinationKind !== value.destinationKind ||
      lineage.destinationDraftId !== value.destinationDraftId ||
      lineage.destinationDraftFingerprint !==
        value.destinationDraftFingerprint
    ) {
      return {
        valid: false,
        reason: "invalid-specialist-location-gateway-terminal-origin",
      };
    }
    const expectedOwnerKey = createSpecialistLocationGatewayOwnerKey({
      stage: "terminal-origin-v1",
      fields: [
        value.terminalKind,
        value.draftId,
        value.receiptFingerprint,
        value.customerAccountId,
        value.destinationKind,
        value.destinationDraftId,
        value.destinationDraftFingerprint,
        value.sourceAppointmentDraftFingerprint,
        value.terminalAtEpochMs,
        lineage.ownerKey,
        value.activationRevision,
      ],
    });
    if (value.ownerKey !== expectedOwnerKey) {
      return {
        valid: false,
        reason: "invalid-specialist-location-gateway-terminal-origin",
      };
    }
    return {
      valid: true,
      draft: {
        schemaVersion:
          SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_SCHEMA_VERSION,
        kind: "specialist-location-gateway-terminal-origin",
        draftId: value.draftId,
        ownerKey: expectedOwnerKey,
        terminalKind: value.terminalKind,
        activationRevision: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
        receiptFingerprint: value.receiptFingerprint,
        customerAccountId: value.customerAccountId,
        destinationKind: value.destinationKind,
        destinationDraftId: value.destinationDraftId,
        destinationDraftFingerprint: value.destinationDraftFingerprint,
        sourceAppointmentDraftFingerprint:
          value.sourceAppointmentDraftFingerprint,
        terminalAtEpochMs: value.terminalAtEpochMs,
        receipt,
        lineage,
      },
    };
  } catch {
    return {
      valid: false,
      reason: "invalid-specialist-location-gateway-terminal-origin",
    };
  }
}

function validateTerminalOrigin(
  value: unknown,
) {
  return validateTerminalOriginForWaitlistMode(value, "individual");
}

export function validateIndividualSpecialistLocationGatewayWaitlistTerminalOriginForStorageMigration(
  value: unknown,
) {
  return validateTerminalOriginForWaitlistMode(value, "individual");
}

/** Canonical legacy proof used only before deleting retired Group rows. */
export function validateRetiredGroupSpecialistLocationGatewayWaitlistTerminalOriginForStorageMigration(
  value: unknown,
) {
  return validateTerminalOriginForWaitlistMode(value, "group");
}

function createTerminalOriginStore(
  storageKey: string,
  storeKind: string,
  beforeAccess?: () => boolean,
) {
  return createVersionedSessionDraftStore<SpecialistLocationGatewayTerminalOriginV1>({
    storageKey,
    storeKind,
    maxEntries: 8,
    maxAgeMs: SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_STORAGE_MAX_AGE_MS,
    maxSerializedCharacters: MAX_SERIALIZED_MARKERS,
    beforeAccess,
    validate: validateTerminalOrigin,
  });
}

const appointmentConfirmationOrigins = createTerminalOriginStore(
  SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_CONFIRMATION_ORIGIN_STORAGE_KEY,
  "specialist-location-gateway-appointment-confirmation-origins",
);
const appointmentWaitlistOrigins = createTerminalOriginStore(
  SPECIALIST_LOCATION_GATEWAY_APPOINTMENT_WAITLIST_ORIGIN_STORAGE_KEY,
  "specialist-location-gateway-appointment-waitlist-origins",
  migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage,
);
const packagePurchaseOrigins = createTerminalOriginStore(
  SPECIALIST_LOCATION_GATEWAY_PACKAGE_PURCHASE_ORIGIN_STORAGE_KEY,
  "specialist-location-gateway-package-purchase-origins",
);

function getTerminalOriginStore(
  terminalKind: SpecialistLocationGatewayTerminalKind,
) {
  switch (terminalKind) {
    case "appointment-confirmation":
      return appointmentConfirmationOrigins;
    case "appointment-waitlist":
      return appointmentWaitlistOrigins;
    case "package-purchase":
      return packagePurchaseOrigins;
  }
}

function cloneTerminalReceipt(
  terminalKind: SpecialistLocationGatewayTerminalKind,
  value: unknown,
  expectedWaitlistMode: "individual" | "group" = "individual",
): SupportedTerminalReceipt | null {
  switch (terminalKind) {
    case "appointment-confirmation": {
      const validation = validateAppointmentBookingReceipt(value);
      return validation.valid ? validation.receipt : null;
    }
    case "appointment-waitlist": {
      const validation =
        expectedWaitlistMode === "individual"
          ? validateAppointmentWaitlistReceipt(value)
          : validateRetiredGroupAppointmentWaitlistReceiptForStorageMigration(
              value,
            );
      return validation.valid ? validation.receipt : null;
    }
    case "package-purchase": {
      const validation = validatePackagePurchaseReceipt(value);
      return validation.valid ? validation.receipt : null;
    }
  }
}

function resolveTerminalReceiptBinding(
  terminalKind: SpecialistLocationGatewayTerminalKind,
  receipt: SupportedTerminalReceipt,
): {
  destinationKind: SpecialistLocationGatewayDestinationKind;
  destinationDraftId: string;
  destinationDraftFingerprint: string;
  sourceAppointmentDraftFingerprint: string | null;
  terminalAtEpochMs: number;
  customerAccountId: string;
} | null {
  switch (terminalKind) {
    case "appointment-confirmation": {
      if (receipt.kind !== "appointment-booking-receipt") return null;
      const appointment =
        receipt.reviewDraft.intakeDraft.schedulingDraft.appointmentDraft;
      if (appointment.entryPoint.kind !== "specialist-services") return null;
      const destinationDraftFingerprint =
        createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint(
          receipt.sourceAppointmentDraftFingerprint,
        );
      return destinationDraftFingerprint
        ? {
            destinationKind: "individual",
            destinationDraftId: receipt.sourceAppointmentDraftId,
            destinationDraftFingerprint,
            sourceAppointmentDraftFingerprint:
              receipt.sourceAppointmentDraftFingerprint,
            terminalAtEpochMs: receipt.createdAtEpochMs,
            customerAccountId: receipt.customerAccountId,
          }
        : null;
    }
    case "appointment-waitlist": {
      if (receipt.kind !== "appointment-waitlist-receipt") return null;
      if (
        receipt.waitlistDraft.appointmentDraft.entryPoint.kind !==
        "specialist-services"
      ) {
        return null;
      }
      const destinationDraftFingerprint =
        createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint(
          receipt.sourceAppointmentDraftFingerprint,
        );
      return destinationDraftFingerprint
        ? {
            // Historical shared Waitlist V3 always used the ordinary
            // Appointment destination label, including retired Group rows.
            destinationKind: "individual",
            destinationDraftId: receipt.sourceAppointmentDraftId,
            destinationDraftFingerprint,
            sourceAppointmentDraftFingerprint:
              receipt.sourceAppointmentDraftFingerprint,
            terminalAtEpochMs: receipt.submission.acceptedAtEpochMs,
            customerAccountId: receipt.customerAccountId,
          }
        : null;
    }
    case "package-purchase": {
      if (
        receipt.kind !== "package-purchase-receipt" ||
        receipt.purchaseDraft.entryPoint.kind !== "specialist-packages"
      ) {
        return null;
      }
      const destinationDraftFingerprint =
        createSpecialistLocationGatewayPackageDestinationFingerprint(
          receipt.purchaseDraft,
        );
      return destinationDraftFingerprint
        ? {
            destinationKind: "package",
            destinationDraftId: receipt.purchaseDraft.draftId,
            destinationDraftFingerprint,
            sourceAppointmentDraftFingerprint: null,
            terminalAtEpochMs: receipt.purchasedAtEpochMs,
            customerAccountId: receipt.customerAccountId,
          }
        : null;
    }
  }
}

function createTerminalOriginForWaitlistMode(input: {
  terminalKind: SpecialistLocationGatewayTerminalKind;
  receipt: unknown;
  lineage: unknown;
  expectedWaitlistMode: "individual" | "group";
}): SpecialistLocationGatewayTerminalOriginV1 | null {
  try {
    const receipt = cloneTerminalReceipt(
      input.terminalKind,
      input.receipt,
      input.expectedWaitlistMode,
    );
    const lineage = cloneCommittedLineage(input.lineage);
    if (!receipt || !lineage) return null;

    const binding = resolveTerminalReceiptBinding(
      input.terminalKind,
      receipt,
    );
    if (!binding) return null;
    const {
      destinationKind,
      destinationDraftId,
      destinationDraftFingerprint,
      sourceAppointmentDraftFingerprint,
      terminalAtEpochMs,
      customerAccountId,
    } = binding;

    if (
      lineage.customerAccountId !== customerAccountId ||
      lineage.destinationKind !== destinationKind ||
      lineage.destinationDraftId !== destinationDraftId ||
      lineage.destinationDraftFingerprint !== destinationDraftFingerprint
    ) {
      return null;
    }

    const receiptFingerprint = createTerminalReceiptFingerprint(
      input.terminalKind,
      receipt,
    );
    const ownerKey = createSpecialistLocationGatewayOwnerKey({
      stage: "terminal-origin-v1",
      fields: [
        input.terminalKind,
        receipt.draftId,
        receiptFingerprint,
        customerAccountId,
        destinationKind,
        destinationDraftId,
        destinationDraftFingerprint,
        sourceAppointmentDraftFingerprint,
        terminalAtEpochMs,
        lineage.ownerKey,
        SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
      ],
    });
    const validation = validateTerminalOriginForWaitlistMode(
      {
        schemaVersion:
          SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_SCHEMA_VERSION,
        kind: "specialist-location-gateway-terminal-origin",
        draftId: receipt.draftId,
        ownerKey,
        terminalKind: input.terminalKind,
        activationRevision: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION,
        receiptFingerprint,
        customerAccountId,
        destinationKind,
        destinationDraftId,
        destinationDraftFingerprint,
        sourceAppointmentDraftFingerprint,
        terminalAtEpochMs,
        receipt,
        lineage,
      },
      input.expectedWaitlistMode,
    );
    return validation.valid ? validation.draft : null;
  } catch {
    return null;
  }
}

function createTerminalOrigin(input: {
  terminalKind: SpecialistLocationGatewayTerminalKind;
  receipt: unknown;
  lineage: unknown;
}) {
  return createTerminalOriginForWaitlistMode({
    ...input,
    expectedWaitlistMode: "individual",
  });
}

export function createRetiredGroupSpecialistLocationGatewayWaitlistTerminalOriginForStorageMigration(
  input: { receipt: unknown; lineage: unknown },
) {
  return createTerminalOriginForWaitlistMode({
    terminalKind: "appointment-waitlist",
    receipt: input.receipt,
    lineage: input.lineage,
    expectedWaitlistMode: "group",
  });
}

function ambiguousStoreStatus(status: string) {
  return (
    status === "unreadable" ||
    status === "persistence-failed" ||
    status === "readback-mismatch"
  );
}

function terminalPersistenceClockIsSafe(terminalAtEpochMs?: number) {
  try {
    const nowEpochMs = Date.now();
    if (
      !Number.isSafeInteger(nowEpochMs) ||
      nowEpochMs < 0 ||
      nowEpochMs > 8_640_000_000_000_000
    ) {
      return false;
    }
    if (terminalAtEpochMs === undefined) return true;
    return (
      nowEpochMs + 5 * 60_000 >= terminalAtEpochMs &&
      nowEpochMs <=
        terminalAtEpochMs +
          SPECIALIST_LOCATION_GATEWAY_TERMINAL_ORIGIN_STORAGE_MAX_AGE_MS
    );
  } catch {
    return false;
  }
}

function safelyInspectReceipt<TReceipt>(
  inspectReceipt: (draftId: string) => PersistedReceiptInspection<TReceipt>,
  draftId: string,
  terminalAtEpochMs: number,
): PersistedReceiptInspection<TReceipt> {
  if (!terminalPersistenceClockIsSafe(terminalAtEpochMs)) {
    return { status: "unreadable" };
  }
  try {
    return inspectReceipt(draftId);
  } catch {
    return { status: "unreadable" };
  }
}

function persistTerminalReceipt<TReceipt extends SupportedTerminalReceipt>(input: {
  terminalKind: SpecialistLocationGatewayTerminalKind;
  receipt: TReceipt;
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  owner: SpecialistLocationGatewayTerminalWriteOwner;
  createReceipt: (receipt: TReceipt) => PersistedReceiptCreation<TReceipt>;
  inspectReceipt: (draftId: string) => PersistedReceiptInspection<TReceipt>;
  removeReceipt: (receipt: TReceipt) => PersistedReceiptRemoval<TReceipt>;
}): TReceipt | null {
  const marker = createTerminalOrigin(input);
  if (!marker) return null;
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([input.terminalKind, input.receipt.draftId]),
    input.owner.isCurrentOwner,
  );
  if (!lease) return null;
  const markerStore = getTerminalOriginStore(input.terminalKind);
  let markerCreated = false;
  let receiptCreated = false;
  let receiptCreationAmbiguous = false;
  const isCurrent = () =>
    terminalPersistenceClockIsSafe(marker.terminalAtEpochMs) &&
    specialistLocationGatewayTerminalWriteIsCurrent(
      lease,
      input.owner.isCurrentOwner,
    );
  const inspectMarker = () => {
    if (!terminalPersistenceClockIsSafe(marker.terminalAtEpochMs)) {
      return { status: "unreadable" as const };
    }
    try {
      return markerStore.inspectPersistedDraft(marker.draftId);
    } catch {
      return { status: "unreadable" as const };
    }
  };
  const removeExactMarker = () => {
    try {
      return markerStore.removeDraftStrictPersisted(marker).status;
    } catch {
      return "unreadable";
    }
  };
  const removeExactReceipt = () => {
    try {
      return input.removeReceipt(input.receipt).status;
    } catch {
      return "unreadable";
    }
  };
  const compensateOwnWrites = () => {
    const receiptRemoved = receiptCreated
      ? removeExactReceipt()
      : "absent";
    const markerRemoved = markerCreated ? removeExactMarker() : "absent";
    const compensated =
      (receiptRemoved === "removed" || receiptRemoved === "absent") &&
      (markerRemoved === "removed" || markerRemoved === "absent");
    if (!compensated) quarantineCustomerOwnedSessionStorage();
    return compensated;
  };
  const finalMarkerIsExact = () => {
    const finalMarker = inspectMarker();
    return (
      finalMarker.status === "present" &&
      JSON.stringify(finalMarker.draft) === JSON.stringify(marker) &&
      isCurrent()
    );
  };

  try {
    if (!isCurrent()) return null;
    const initialReceipt = safelyInspectReceipt(
      input.inspectReceipt,
      input.receipt.draftId,
      marker.terminalAtEpochMs,
    );
    if (!isCurrent()) return null;
    if (initialReceipt.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return null;
    }
    if (
      initialReceipt.status === "present" &&
      JSON.stringify(initialReceipt.draft) !== JSON.stringify(input.receipt)
    ) {
      quarantineCustomerOwnedSessionStorage();
      return null;
    }

    const existing = inspectMarker();
    if (!isCurrent()) return null;
    if (existing.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return null;
    }
    if (
      existing.status === "present" &&
      JSON.stringify(existing.draft) !== JSON.stringify(marker)
    ) {
      quarantineCustomerOwnedSessionStorage();
      return null;
    }
    if (existing.status === "absent") {
      if (!isCurrent()) return null;
      let created: ReturnType<typeof markerStore.createDraftStrictPersisted>;
      try {
        created = markerStore.createDraftStrictPersisted(marker);
      } catch {
        created = { status: "unreadable" };
      }
      if (!isCurrent()) {
        markerCreated = created.status === "created";
        compensateOwnWrites();
        return null;
      }
      if (created.status === "created") {
        markerCreated = true;
      } else if (created.status === "conflict") {
        const raced = inspectMarker();
        if (
          raced.status !== "present" ||
          JSON.stringify(raced.draft) !== JSON.stringify(marker)
        ) {
          quarantineCustomerOwnedSessionStorage();
          return null;
        }
      } else {
        if (ambiguousStoreStatus(created.status)) {
          quarantineCustomerOwnedSessionStorage();
        }
        return null;
      }
    }

    if (!isCurrent()) {
      compensateOwnWrites();
      return null;
    }
    if (initialReceipt.status === "present") {
      if (finalMarkerIsExact()) return input.receipt;
      compensateOwnWrites();
      quarantineCustomerOwnedSessionStorage();
      return null;
    }

    if (!markerCreated) {
      // A marker-only crash snapshot is already the authoritative terminal
      // receipt. Refresh its exact envelope so its retention starts with this
      // successful recovery; do not create a second cross-store authority.
      let refreshed: ReturnType<typeof markerStore.replaceDraftStrictPersisted>;
      try {
        refreshed = markerStore.replaceDraftStrictPersisted(marker, marker);
      } catch {
        refreshed = { status: "unreadable" };
      }
      if (!isCurrent()) return null;
      if (refreshed.status !== "replaced") {
        const raced = inspectMarker();
        if (
          raced.status !== "present" ||
          JSON.stringify(raced.draft) !== JSON.stringify(marker)
        ) {
          quarantineCustomerOwnedSessionStorage();
          return null;
        }
      }
      if (finalMarkerIsExact()) return input.receipt;
      quarantineCustomerOwnedSessionStorage();
      return null;
    }

    let createdReceipt: PersistedReceiptCreation<TReceipt>;
    try {
      createdReceipt = input.createReceipt(input.receipt);
    } catch {
      createdReceipt = { status: "unreadable" };
      // Exact persisted inspection below distinguishes success, deterministic
      // absence and an ambiguous/unreadable receipt boundary.
    }
    receiptCreated = createdReceipt.status === "created";
    receiptCreationAmbiguous = ambiguousStoreStatus(createdReceipt.status);
    if (!isCurrent()) {
      if (receiptCreationAmbiguous) {
        quarantineCustomerOwnedSessionStorage();
      }
      compensateOwnWrites();
      return null;
    }
    const receiptInspection = safelyInspectReceipt(
      input.inspectReceipt,
      input.receipt.draftId,
      marker.terminalAtEpochMs,
    );
    if (!isCurrent()) {
      compensateOwnWrites();
      return null;
    }
    if (
      receiptInspection.status === "present" &&
      JSON.stringify(receiptInspection.draft) === JSON.stringify(input.receipt)
    ) {
      // No callbacks or other stores are consulted after this final exact
      // marker proof. Receipt success is publishable only as one durable pair.
      if (finalMarkerIsExact()) return input.receipt;
      compensateOwnWrites();
      quarantineCustomerOwnedSessionStorage();
      return null;
    }
    if (receiptInspection.status !== "absent") {
      quarantineCustomerOwnedSessionStorage();
      return null;
    }
    if (!markerCreated) {
      quarantineCustomerOwnedSessionStorage();
      return null;
    }
    if (removeExactMarker() !== "removed") {
      quarantineCustomerOwnedSessionStorage();
    }
    return null;
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

function currentAppointmentLineageFromReceipt(
  receipt: AppointmentBookingReceiptV2 | AppointmentWaitlistReceiptV3,
) {
  const appointment =
    receipt.kind === "appointment-booking-receipt"
      ? receipt.reviewDraft.intakeDraft.schedulingDraft.appointmentDraft
      : receipt.waitlistDraft.appointmentDraft;
  const result = readSpecialistLocationGatewayAppointmentLineageByEvidence({
    destinationDraftId: receipt.sourceAppointmentDraftId,
    sourceAppointmentDraftFingerprint:
      receipt.sourceAppointmentDraftFingerprint,
    entryPoint: appointment.entryPoint,
    expectedCustomerAccountId: receipt.customerAccountId,
  });
  return result;
}

export function storeAppointmentBookingReceiptWithSpecialistLocationGatewayOrigin(
  value: unknown,
  owner?: SpecialistLocationGatewayTerminalWriteOwner,
) {
  let validation: ReturnType<typeof validateAppointmentBookingReceipt>;
  try {
    validation = validateAppointmentBookingReceipt(value);
  } catch {
    return null;
  }
  if (!validation.valid) return null;
  const receipt = validation.receipt;
  const appointment =
    receipt.reviewDraft.intakeDraft.schedulingDraft.appointmentDraft;
  if (appointment.entryPoint.kind !== "specialist-services") {
    return storeAppointmentBookingReceipt(receipt);
  }
  let lineage: ReturnType<typeof currentAppointmentLineageFromReceipt>;
  try {
    lineage = currentAppointmentLineageFromReceipt(receipt);
  } catch {
    return null;
  }
  if (lineage.status === "ready" && owner) {
    return persistTerminalReceipt({
      terminalKind: "appointment-confirmation",
      receipt,
      lineage: lineage.lineage,
      owner,
      createReceipt: createAppointmentBookingReceiptStrictPersisted,
      inspectReceipt: inspectAppointmentBookingReceiptPersisted,
      removeReceipt: removeAppointmentBookingReceiptStrictPersisted,
    });
  }
  if (lineage.status === "ready") return null;
  if (lineage.reason === "lineage-absent") {
    return owner?.requiresGatewayOrigin ||
      canActivatePublicSpecialistLocationGateway()
      ? null
      : storeAppointmentBookingReceipt(receipt);
  }
  if (lineage.reason === "lineage-unreadable") {
    quarantineCustomerOwnedSessionStorage();
  }
  return null;
}

export function storeAppointmentWaitlistReceiptWithSpecialistLocationGatewayOrigin(
  value: unknown,
  owner?: SpecialistLocationGatewayTerminalWriteOwner,
) {
  let validation: ReturnType<typeof validateAppointmentWaitlistReceipt>;
  try {
    validation = validateAppointmentWaitlistReceipt(value);
  } catch {
    return null;
  }
  if (!validation.valid) return null;
  const receipt = validation.receipt;
  if (
    receipt.waitlistDraft.appointmentDraft.entryPoint.kind !==
    "specialist-services"
  ) {
    return storeAppointmentWaitlistReceipt(receipt);
  }
  let lineage: ReturnType<typeof currentAppointmentLineageFromReceipt>;
  try {
    lineage = currentAppointmentLineageFromReceipt(receipt);
  } catch {
    return null;
  }
  if (lineage.status === "ready" && owner) {
    return persistTerminalReceipt({
      terminalKind: "appointment-waitlist",
      receipt,
      lineage: lineage.lineage,
      owner,
      createReceipt: createAppointmentWaitlistReceiptStrictPersisted,
      inspectReceipt: inspectAppointmentWaitlistReceiptPersisted,
      removeReceipt: removeAppointmentWaitlistReceiptStrictPersisted,
    });
  }
  if (lineage.status === "ready") return null;
  if (lineage.reason === "lineage-absent") {
    return owner?.requiresGatewayOrigin ||
      canActivatePublicSpecialistLocationGateway()
      ? null
      : storeAppointmentWaitlistReceipt(receipt);
  }
  if (lineage.reason === "lineage-unreadable") {
    quarantineCustomerOwnedSessionStorage();
  }
  return null;
}

export function storePackagePurchaseReceiptWithSpecialistLocationGatewayOrigin(
  value: unknown,
  owner?: SpecialistLocationGatewayTerminalWriteOwner,
) {
  let validation: ReturnType<typeof validatePackagePurchaseReceipt>;
  try {
    validation = validatePackagePurchaseReceipt(value);
  } catch {
    return null;
  }
  if (!validation.valid) return null;
  const receipt = validation.receipt;
  if (receipt.purchaseDraft.entryPoint.kind !== "specialist-packages") {
    return storePackagePurchaseReceipt(receipt);
  }
  let lineage: ReturnType<
    typeof readSpecialistLocationGatewayPackageDestinationPair
  >;
  try {
    lineage = readSpecialistLocationGatewayPackageDestinationPair({
      draft: receipt.purchaseDraft,
      expectedCustomerAccountId: receipt.customerAccountId,
    });
  } catch {
    return null;
  }
  if (lineage.status === "ready" && owner) {
    return persistTerminalReceipt({
      terminalKind: "package-purchase",
      receipt,
      lineage: lineage.lineage,
      owner,
      createReceipt: createPackagePurchaseReceiptStrictPersisted,
      inspectReceipt: inspectPackagePurchaseReceiptPersisted,
      removeReceipt: removePackagePurchaseReceiptStrictPersisted,
    });
  }
  if (lineage.status === "ready") return null;
  if (lineage.reason === "lineage-absent") {
    return owner?.requiresGatewayOrigin ||
      canActivatePublicSpecialistLocationGateway()
      ? null
      : storePackagePurchaseReceipt(receipt);
  }
  if (lineage.reason === "lineage-unreadable") {
    quarantineCustomerOwnedSessionStorage();
  }
  return null;
}

export function readSpecialistLocationGatewayTerminalOriginStrict(input: {
  terminalKind: SpecialistLocationGatewayTerminalKind;
  receipt: unknown;
}):
  | {
      status: "ready";
      origin: SpecialistLocationGatewayTerminalOriginV1;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" } {
  let receipt: SupportedTerminalReceipt | null = null;
  try {
    receipt = cloneTerminalReceipt(input.terminalKind, input.receipt);
  } catch {
    return { status: "owner-mismatch" };
  }
  if (!receipt) return { status: "owner-mismatch" };
  const store = getTerminalOriginStore(input.terminalKind);
  let persisted: ReturnType<typeof store.inspectPersistedDraft>;
  try {
    persisted = store.inspectPersistedDraft(receipt.draftId);
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" };
  }
  if (persisted.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" };
  }
  if (persisted.status === "absent") return { status: "absent" };
  let expected: SpecialistLocationGatewayTerminalOriginV1 | null = null;
  try {
    expected = createTerminalOrigin({
      terminalKind: input.terminalKind,
      receipt,
      lineage: persisted.draft.lineage,
    });
  } catch {
    expected = null;
  }
  if (
    !expected ||
    JSON.stringify(expected) !== JSON.stringify(persisted.draft)
  ) {
    quarantineCustomerOwnedSessionStorage();
    return { status: "owner-mismatch" };
  }
  return {
    status: "ready",
    origin: persisted.draft,
    lineage: persisted.draft.lineage,
  };
}

/**
 * Reads the authoritative single-envelope terminal snapshot. Receipt routes
 * use this when SLG is active so a missing compatibility receipt projection
 * can never erase already-accepted terminal evidence.
 */
export function readSpecialistLocationGatewayTerminalReceiptStrict(input: {
  terminalKind: SpecialistLocationGatewayTerminalKind;
  draftId: string;
  expectedCustomerAccountId: string;
}):
  | {
      status: "ready";
      receipt: SupportedTerminalReceipt;
      origin: SpecialistLocationGatewayTerminalOriginV1;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" } {
  if (
    !isIdentifier(input.draftId) ||
    !isIdentifier(input.expectedCustomerAccountId)
  ) {
    return { status: "owner-mismatch" };
  }
  const store = getTerminalOriginStore(input.terminalKind);
  let persisted: ReturnType<typeof store.inspectPersistedDraft>;
  try {
    persisted = store.inspectPersistedDraft(input.draftId);
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" };
  }
  if (persisted.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" };
  }
  if (persisted.status === "absent") return { status: "absent" };
  if (
    persisted.draft.terminalKind !== input.terminalKind ||
    persisted.draft.customerAccountId !== input.expectedCustomerAccountId
  ) {
    return { status: "owner-mismatch" };
  }
  return {
    status: "ready",
    receipt: persisted.draft.receipt,
    origin: persisted.draft,
    lineage: persisted.draft.lineage,
  };
}

/** Tagged, owner-agnostic projection inspection used only by canonical-store
 * downgrade guards. A present legacy marker is a tombstone, never authority. */
export function inspectSpecialistLocationGatewayTerminalOriginPersisted(input: {
  terminalKind: SpecialistLocationGatewayTerminalKind;
  draftId: string;
  evaluatedAtEpochMs?: number;
}): { status: "absent" | "present" | "unreadable" } {
  if (!isIdentifier(input.draftId)) return { status: "unreadable" };
  let store: ReturnType<typeof getTerminalOriginStore>;
  try {
    store = getTerminalOriginStore(input.terminalKind);
    const persisted = store.inspectPersistedDraft(
      input.draftId,
      input.evaluatedAtEpochMs,
    );
    if (persisted.status === "unreadable") {
      quarantineCustomerOwnedSessionStorage();
      return { status: "unreadable" };
    }
    return { status: persisted.status === "absent" ? "absent" : "present" };
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return { status: "unreadable" };
  }
}

export function clearSpecialistLocationGatewayTerminalOrigins() {
  let appointmentConfirmationOriginsCleared = false;
  let appointmentWaitlistOriginsCleared = false;
  let packagePurchaseOriginsCleared = false;
  try {
    appointmentConfirmationOriginsCleared =
      appointmentConfirmationOrigins.clearDrafts();
  } catch {
    appointmentConfirmationOriginsCleared = false;
  }
  try {
    appointmentWaitlistOriginsCleared =
      appointmentWaitlistOrigins.clearDrafts();
  } catch {
    appointmentWaitlistOriginsCleared = false;
  }
  try {
    packagePurchaseOriginsCleared = packagePurchaseOrigins.clearDrafts();
  } catch {
    packagePurchaseOriginsCleared = false;
  }

  return (
    appointmentConfirmationOriginsCleared &&
    appointmentWaitlistOriginsCleared &&
    packagePurchaseOriginsCleared
  );
}
